// src/pages/AllEmployees.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AllEmployees.css";
import { getRole } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const AllEmployees = () => {
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [confirmActionData, setConfirmActionData] = useState(null);

  const [departmentFilter, setDepartmentFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [loggedInRole, setLoggedInRole] = useState("");

  const departmentOptions = [
    "Production Support",
    "Help Desk",
    "MSBI",
    "DBA",
    "Fullstack",
    "HR Manager",
  ];

  // SAFE fallback (prevents runtime crashes)
  const emp = selectedEmployee || {};

  useEffect(() => {
    const role = getRole();
    if (role) setLoggedInRole(role);
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const data = await apiClient(
        "/auth/allemployees/"
      );

      const list = Array.isArray(data)
        ? data
        : data?.results || data?.employees || [];

      setEmployees(list);
      setFilteredEmployees(list);
    } catch (err) {
      console.error(err);
      setEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = employees;

    if (departmentFilter) {
      filtered = filtered.filter(
        (e) =>
          e.department?.toLowerCase() === departmentFilter.toLowerCase()
      );
    }

    if (nameFilter) {
      filtered = filtered.filter((e) =>
        e.name?.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    setFilteredEmployees(filtered);
  }, [departmentFilter, nameFilter, employees]);

  const handleViewMore = (emp) => setSelectedEmployee(emp);
  const closeModal = () => setSelectedEmployee(null);

  const confirmAction = (action, employee) =>
    setConfirmActionData({ action, employee });

  const handleConfirmYes = async () => {
    if (!confirmActionData) return;
    await toggleActivation(
      confirmActionData.employee,
      confirmActionData.action
    );
    setConfirmActionData(null);
  };

  const toggleActivation = async (employee, action) => {
    try {
      const data = await apiClient(
        "/auth/employees/activate-deactivate/",
        {
          method: "POST",
          body: JSON.stringify({ user_id: employee.id, action }),
        }
      );

      setPopupMessage(data?.detail || "Status updated");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);

      setEmployees((prev) =>
        prev.map((e) =>
          e.id === employee.id ? { ...e, is_active: data.is_active } : e
        )
      );
    } catch {
      setPopupMessage("Failed to update status");
      setShowPopup(true);
    }
  };

  const handleEdit = (emp) =>
    navigate(`/dashboard/ManagerAccess/editemployee/${emp.id}`);

  if (loading) return <p>Loading employee data...</p>;

  return (
    <div className="employee-table-container">
      {showPopup && (
        <div className="popup-message">
          {popupMessage}
          <button onClick={() => setShowPopup(false)}>×</button>
        </div>
      )}

      {/* Confirm Activate/Deactivate */}
      {confirmActionData && (
        <div className="modal-backdrop">
          <div className="modal-content1">
            <h3>Confirm Action</h3>
            <p>
              Are you sure you want to{" "}
              <strong>{confirmActionData.action}</strong> this employee?
            </p>
            <button onClick={handleConfirmYes}>Yes</button>
            <button onClick={() => setConfirmActionData(null)}>No</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="header-with-button">
        <h2>All Employees</h2>
        <button
          className="add-btn1"
          onClick={() => navigate("/dashboard/ManagerAcess/newemployee")}
        >
          +
        </button>
      </div>

      {/* Filters */}
      <div className="filters1">
        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
        >
          <option value="">All Departments</option>
          {departmentOptions.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </select>

        <input
          placeholder="Search by Name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
      </div>

      {/* Table */}
      <table className="employee-table">
        <thead>
          <tr>
            <th>Employee ID</th>
            <th>Name</th>
            <th>Department</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredEmployees.map((emp, i) => (
            <tr key={emp.id || i}>
              <td>{emp.employee_id || i + 1}</td>
              <td>{emp.name}</td>
              <td>{emp.department}</td>
              <td>
                <button onClick={() => handleViewMore(emp)}>View More</button>
                <button onClick={() => handleEdit(emp)}>Edit</button>

                {loggedInRole === "admin" &&
                  (emp.is_active ? (
                    <button
                      style={{ background: "red" }}
                      onClick={() => confirmAction("deactivate", emp)}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      style={{ background: "green" }}
                      onClick={() => confirmAction("activate", emp)}
                    >
                      Activate
                    </button>
                  ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* VIEW MORE MODAL */}
      {selectedEmployee && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Employee Details</h3>

            {/* Personal Information */}
            <div className="details-section">
              <h4>Personal Information</h4>
              <div className="details-grid">
                <div><strong>Email:</strong><span>{emp.email || "N/A"}</span></div>
                <div><strong>Name:</strong><span>{emp.name || "N/A"}</span></div>
                <div><strong>Phone:</strong><span>{emp.phone || "N/A"}</span></div>
                <div><strong>Secondary Phone:</strong><span>{emp.secondary_phone || "N/A"}</span></div>
                <div><strong>Emergency Contact:</strong><span>{emp.emergency_contact || "N/A"}</span></div>
                <div><strong>Gender:</strong><span>{emp.gender || "N/A"}</span></div>
                <div><strong>Joining Date:</strong><span>{emp.joining_date || "N/A"}</span></div>
                <div><strong>Probation End:</strong><span>{emp.probation_end_date || "N/A"}</span></div>
                <div><strong>DOB:</strong><span>{emp.dob || "N/A"}</span></div>
                <div><strong>Blood Group:</strong><span>{emp.blood_group || "N/A"}</span></div>
                <div className="full-width">
                  <strong>Address:</strong>
                  <span>{emp.address || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Work Information */}
            <div className="details-section">
              <h4>Work Information</h4>
              <div className="details-grid">
                <div><strong>Designation:</strong><span>{emp.designation || "N/A"}</span></div>
                <div><strong>Department:</strong><span>{emp.department || "N/A"}</span></div>
                <div><strong>Work Type:</strong><span>{emp.work_type || "N/A"}</span></div>
                <div><strong>Working Mode:</strong><span>{emp.working_mode || "N/A"}</span></div>
                <div><strong>Reports To:</strong><span>{emp.report_to || "N/A"}</span></div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={closeModal}>Close</button>
              <button onClick={() => handleEdit(emp)}>Edit</button>
            </div>
          </div>
        </div>
      )}

      <SessionHandler />
    </div>
  );
};

export default AllEmployees;
